import { set, type FieldValues, type Path, type Resolver, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { errorMessage, HttpError } from '@/lib/api';

/** Resolver Zod com normalização dos valores do formulário ('' -> null, máscara -> centavos) antes de validar com os contracts. */
export function zodForm<T extends FieldValues>(schema: z.ZodType, normalize?: (v: T) => unknown, rename: Record<string, string> = {}): Resolver<T> {
  return async (values) => {
    const r = schema.safeParse(normalize ? normalize(values) : values);
    if (r.success) return { values, errors: {} };
    const errors: Record<string, unknown> = {};
    for (const issue of r.error.issues) {
      const raw = issue.path.join('.');
      const path = rename[raw] ?? raw;
      if (!path || (errors as Record<string, unknown>)[path]) continue;
      set(errors, path, { type: issue.code, message: issue.message });
    }
    return { values: {}, errors } as never;
  };
}

/** Erros do servidor viram erro de campo quando possível; o resto vira toast. */
export function applyServerError<T extends FieldValues>(form: UseFormReturn<T>, e: unknown, rename: Record<string, string> = {}) {
  if (e instanceof HttpError) {
    if (e.code === 'duplicate_phone') return void form.setError('phone' as Path<T>, { type: 'server', message: e.message });
    if (e.code === 'duplicate_email') return void form.setError('email' as Path<T>, { type: 'server', message: e.message });
    if (e.code === 'duplicate_code') return void form.setError('code' as Path<T>, { type: 'server', message: e.message });
    const known = Object.entries(e.fieldErrors()).filter(([k]) => k);
    if (known.length) {
      for (const [k, msg] of known) form.setError((rename[k] ?? k) as Path<T>, { type: 'server', message: msg });
      return;
    }
  }
  toast.error(errorMessage(e));
}

export const emptyToNull = (v: string | null | undefined) => (v && v.trim() ? v.trim() : null);

/** AAAA-MM-DD -> dd/MM/aaaa (sem fuso: é data de calendário). */
export const fmtIsoDate = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '—');
